import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import { OpenInNew, SwipeLeftAlt } from '@mui/icons-material';
import { getComparator } from '../../utils/tableSort';

const fmtCount = (v) => (v ?? 0).toLocaleString();
const fmtPct = (v) => v != null ? `${(v * 100).toFixed(2)}%` : '—';
const fmtHours = (h) => {
  if (h == null) return '—';
  if (h < 1) return `${Math.round(h * 60)}m`;
  return `${h.toFixed(1)}h`;
};

const COLUMNS = [
  { id: 'retailer_name', label: 'Retailer', align: 'left', get: (r) => r.retailer_name, sx: { minWidth: 120 } },
  { id: 'recv_clicks', label: 'Recv clicks', align: 'right', get: (r) => r.received?.clicks ?? 0, format: fmtCount },
  { id: 'recv_orders', label: 'Recv orders', align: 'right', get: (r) => r.received?.orders ?? 0, format: fmtCount },
  { id: 'recv_conv', label: 'Recv conv%', align: 'right', get: (r) => r.received?.conversion_rate ?? 0, format: fmtPct },
  { id: 'sent_clicks', label: 'Sent clicks', align: 'right', get: (r) => r.sent?.clicks ?? 0, format: fmtCount, sx: { borderLeft: '2px solid', borderColor: 'divider' } },
  { id: 'sent_orders', label: 'Sent orders', align: 'right', get: (r) => r.sent?.orders ?? 0, format: fmtCount },
  { id: 'sent_conv', label: 'Sent conv%', align: 'right', get: (r) => r.sent?.conversion_rate ?? 0, format: fmtPct },
  { id: 'sent_avg_time', label: 'Avg t (sent)', align: 'right', get: (r) => r.sent?.avg_conversion_hours ?? null, format: fmtHours, sx: { borderLeft: '2px solid', borderColor: 'divider' } },
  { id: 'sent_p50_time', label: 'p50 t (sent)', align: 'right', get: (r) => r.sent?.p50_conversion_hours ?? null, format: fmtHours },
  { id: 'sent_p90_time', label: 'p90 t (sent)', align: 'right', get: (r) => r.sent?.p90_conversion_hours ?? null, format: fmtHours },
  { id: 'recv_avg_time', label: 'Avg t (recv)', align: 'right', get: (r) => r.received?.avg_conversion_hours ?? null, format: fmtHours, sx: { borderLeft: '2px solid', borderColor: 'divider' } },
  { id: 'recv_p50_time', label: 'p50 t (recv)', align: 'right', get: (r) => r.received?.p50_conversion_hours ?? null, format: fmtHours },
  { id: 'recv_p90_time', label: 'p90 t (recv)', align: 'right', get: (r) => r.received?.p90_conversion_hours ?? null, format: fmtHours },
];

const RetailerClickTable = ({ retailers, onDrilldown }) => {
  const [orderBy, setOrderBy] = useState('sent_clicks');
  const [order, setOrder] = useState('desc');

  const handleSort = (columnId) => {
    const isDesc = orderBy === columnId && order === 'desc';
    setOrder(isDesc ? 'asc' : 'desc');
    setOrderBy(columnId);
  };

  // Flatten nested fields for sorting
  const flatRows = useMemo(
    () => (retailers || []).map((r) => {
      const flat = { ...r };
      COLUMNS.forEach((col) => { flat[col.id] = col.get(r); });
      return flat;
    }),
    [retailers],
  );

  const sorted = useMemo(
    () => [...flatRows].sort(getComparator(order, orderBy)),
    [flatRows, order, orderBy],
  );

  const visible = sorted.filter(
    (r) => (r.sent?.clicks ?? r.sent_clicks ?? 0) > 0 || (r.received?.clicks ?? r.recv_clicks ?? 0) > 0,
  );

  if (!visible.length) {
    return (
      <Paper sx={{ p: 2 }} elevation={1}>
        <Typography variant="h6" gutterBottom>Retailer click performance</Typography>
        <Typography color="text.secondary">No click data for this period.</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: { xs: 1, sm: 2 } }} elevation={1}>
      <Typography variant="h6" gutterBottom>Retailer click performance</Typography>
      <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 0.5, justifyContent: 'flex-end', mb: 0.5 }}>
        <SwipeLeftAlt sx={{ fontSize: 16, color: 'text.disabled' }} />
        <Typography variant="caption" color="text.disabled">Scroll for more</Typography>
      </Box>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' }, '& td:not(:first-of-type), & th:not(:first-of-type)': { px: 1 } }}>
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell key={col.id} align={col.align} sx={col.sx}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'desc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="right" sx={{ px: 1 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((row) => (
              <TableRow key={row.retailer_id} hover>
                {COLUMNS.map((col) => (
                  <TableCell key={col.id} align={col.align} sx={col.sx}>
                    {col.format ? col.format(row[col.id]) : row[col.id]}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ px: 0.5 }}>
                  <Tooltip title={`Partner breakdown for ${row.retailer_name}`}>
                    <IconButton size="small" onClick={() => onDrilldown?.(row.retailer_id, row.retailer_name)}>
                      <OpenInNew sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default RetailerClickTable;
